// ============================================
// Creator Autofill - Handler (ES6)
// 자동채우기 실행 및 결과 적용
// ============================================

import { showToast } from '../../utils.js';
import { CURRENCY } from '../../constants.js';
import { getCurrentUser } from '../../app/state.js';
import { ENDPOINTS } from '../api.js';
import { authFetch } from '../../shared/auth-fetch.js';
import { getCurrentEditorData, setIntroBlocks, setCache, setExampleDialogues, getExampleDialogues } from '../state.js';
import { addEditorImage, updatePublishButtonState, updateImageGenButtonState, parseFirstMessageToBlocks } from '../editor.js';
import { renderIntroPreview } from '../intro-blocks.js';
import { saveCreationData } from '../save.js';
import { showLoadingWithMessages, hideLoadingWithMessages } from '../../shared/autofill-utils.js';
import { closeAutofillModal } from './modal.js';
import { updateCarbonDisplay } from '../../auth/index.js';
import { updateCharCounts, updateAllFieldValidationStates, updateTabCheckStatus, updateExampleDialogueTotalCount } from '../editor/events.js';
import { renderExampleDialogueList } from '../editor/form.js';
import { updateCustomDescStatus, updateCustomDescButton } from '../editor/custom-desc.js';

// ============================================
// 로딩 래퍼 (기존 호환성)
// ============================================

export function showLoadingWithRandomText(type = 'autofill') {
  showLoadingWithMessages(type);
}

export function hideLoadingWithRandomText() {
  hideLoadingWithMessages();
}

// ============================================
// 결과 적용 (공유 유틸리티)
// ============================================

export function applyAutoFilledData(data, emptyFields, needsIntro, hasImage) {
  const char = data.character || {};

  const setValueIfEmpty = (fieldId, value, fieldKey) => {
    if (emptyFields.includes(fieldKey) && value) {
      const el = document.getElementById(fieldId);
      if (el) el.value = value;
    }
  };

  // 새 필드 구조: name, age/experience, relationship, prompt, worldPrompt
  setValueIfEmpty('editorCharName', char.name, 'name');
  setValueIfEmpty('editorCharAge', char.age, 'age');
  setValueIfEmpty('editorExperience', char.experience, 'experience');
  setValueIfEmpty('editorCharRelationship', char.relationship, 'relationship');
  setValueIfEmpty('editorCharPrompt', char.prompt, 'prompt');
  setValueIfEmpty('editorWorldPrompt', char.worldPrompt, 'worldPrompt');

  if (needsIntro && data.firstMessage) {
    setIntroBlocks(parseFirstMessageToBlocks(data.firstMessage, data.introImages || []));
    renderIntroPreview();
  }

  // 성별 적용 (비어있을 때만)
  const genderEl = document.getElementById('editorCharGender');
  if (genderEl && data.gender) {
    // 현재 값이 기본값이거나 비어있으면 적용
    genderEl.value = data.gender;
  }

  // 타겟 오디언스 적용 (비어있거나 기본값일 때만)
  const targetEl = document.getElementById('editorTargetAudience');
  if (targetEl && data.targetAudience) {
    // all이 기본값이므로 새 값으로 덮어씀
    if (targetEl.value === 'all' || !targetEl.value) {
      targetEl.value = data.targetAudience;
    }
  }

  // 음성 ID 적용 (비어있을 때만)
  const voiceEl = document.getElementById('editorVoiceId');
  if (voiceEl && data.voiceId) {
    if (!voiceEl.value) {
      voiceEl.value = data.voiceId;
    }
  }

  const shortIntroEl = document.getElementById('editorShortIntro');
  const hashtagsEl = document.getElementById('editorHashtags');

  if (shortIntroEl && !shortIntroEl.value.trim() && data.shortDesc) {
    shortIntroEl.value = data.shortDesc;
  }

  if (hashtagsEl && !hashtagsEl.value.trim()) {
    if (data.tags && Array.isArray(data.tags) && data.tags.length > 0) {
      hashtagsEl.value = data.tags.map(t => t.startsWith('#') ? t : `#${t}`).join(' ');
    }
  }

  // 커스텀 설명 적용 (비어있을 때만)
  if (data.charDescription) {
    const charDescEl = document.getElementById('editorCharDescription');
    if (charDescEl && !charDescEl.value.trim()) {
      charDescEl.value = data.charDescription;
      // 상태 및 버튼 업데이트
      updateCustomDescStatus();
      updateCustomDescButton();
    }
  }

  if (!hasImage && data.imageUrl) {
    addEditorImage(data.imageUrl, null);
    const currentEditorData = getCurrentEditorData();
    if (!currentEditorData.generatedImages) {
      currentEditorData.generatedImages = [];
    }
    currentEditorData.generatedImages.unshift({
      url: data.imageUrl,
      name: `autofill_${Date.now()}.png`,
      createdAt: new Date().toISOString()
    });
    setCache('generatedImages', currentEditorData.generatedImages);
  }

  // 대화 예시 적용 (비어있을 때만)
  if (data.exampleDialogues && Array.isArray(data.exampleDialogues) && data.exampleDialogues.length > 0) {
    const currentDialogues = getExampleDialogues();
    if (!currentDialogues || currentDialogues.length === 0) {
      setExampleDialogues(data.exampleDialogues);
      renderExampleDialogueList();
      updateExampleDialogueTotalCount();
    }
  }

  // 자동채우기 후 모든 상태 업데이트
  updateCharCounts();
  updateAllFieldValidationStates();
  updateTabCheckStatus();
  updatePublishButtonState();
}

// ============================================
// 자동채우기 실행
// ============================================

export async function confirmAutofill() {
  closeAutofillModal();

  const btn = document.querySelector('.editor-autofill-btn');
  if (btn) {
    btn.disabled = true;
    btn.classList.add('loading');
  }

  const autoGenerateImage = document.getElementById('autofillAutoImage')?.checked || false;
  const selectedModelId = document.getElementById('autofillSelectedModel')?.value || '';
  const tagToggle = document.getElementById('autofillTagToggle')?.checked || false;
  let genres = null;
  if (tagToggle) {
    const genresRaw = document.getElementById('autofillSelectedGenres')?.value || '[]';
    try {
      genres = JSON.parse(genresRaw);
    } catch (e) {
      genres = [];
    }
  }

  // 새 필드 구조: name, age, relationship, prompt, worldPrompt
  const existingData = {
    name: document.getElementById('editorCharName')?.value?.trim() || '',
    gender: document.getElementById('editorCharGender')?.value || '여성',
    age: document.getElementById('editorCharAge')?.value?.trim() || '',
    relationship: document.getElementById('editorCharRelationship')?.value?.trim() || '',
    prompt: document.getElementById('editorCharPrompt')?.value?.trim() || '',
    worldPrompt: document.getElementById('editorWorldPrompt')?.value?.trim() || ''
  };

  const hasImage = !!getCurrentEditorData().imageUrl;
  const emptyFields = Object.entries(existingData)
    .filter(([key, val]) => !val && key !== 'gender')
    .map(([key]) => key);

  const introBlocks = window.CreatorModule?.introBlocks || [];
  const needsIntro = introBlocks.length === 0;
  const needsImage = !hasImage && autoGenerateImage;

  if (emptyFields.length === 0 && !needsIntro && (hasImage || !autoGenerateImage)) {
    showToast('모든 필드가 이미 채워져 있습니다');
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
    return;
  }

  showLoadingWithRandomText();

  try {
    const showThoughts = document.getElementById('editorShowThoughts')?.checked || false;

    const response = await authFetch(ENDPOINTS.autoFillCreation, {
      body: {
        existingData: existingData,
        emptyFields: emptyFields,
        needsImage: needsImage,
        needsIntro: needsIntro,
        showThoughts: showThoughts,
        imageModelId: selectedModelId || null,
        genres: genres && genres.length > 0 ? genres : null
      }
    });

    const result = await response.json();

    // 에러 응답 처리 (response.ok 체크)
    if (!response.ok) {
      if (response.status === 402) {
        showToast(`${CURRENCY.NAME}가 부족합니다 (필요: ${result.required}, 현재: ${result.current})`);
      } else {
        // error가 객체일 수 있음 (인증 에러 등)
        const errorMsg = typeof result.error === 'string'
          ? result.error
          : (result.error?.message || '자동 채우기 중 오류가 발생했습니다');
        showToast(errorMsg);
      }
      return;
    }

    if (result.success && result.data) {
      applyAutoFilledData(result.data, emptyFields, needsIntro, !needsImage && hasImage);

      const loadingEl = document.querySelector('.global-loading-text');
      if (loadingEl) loadingEl.textContent = '저장 중...';

      await saveCreationData('draft', { silent: true, skipClose: true });

      if (result.data.imageRefunded) {
        showToast('자동 채우기 완료 (이미지 생성 실패로 이미지 비용 환불됨)');
      } else {
        showToast('자동 채우기가 완료되었습니다!');
      }

      if (typeof result.carbon === 'number') {
        updateCarbonDisplay(result.carbon);
      }
    }
  } catch (error) {
    console.error('자동 채우기 오류:', error);
    showToast(error.message || '자동 채우기 중 오류가 발생했습니다');
  } finally {
    hideLoadingWithRandomText();
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('loading');
    }
    updateImageGenButtonState();
  }
}
